import { Editor } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import Highlight from '@tiptap/extension-highlight';
import TextAlign from '@tiptap/extension-text-align';
import { FontFamily } from '@tiptap/extension-font-family';
import Subscript from '@tiptap/extension-subscript';
import Superscript from '@tiptap/extension-superscript';
import Placeholder from '@tiptap/extension-placeholder';
import { FontSize } from './fontSize';
import { Indent } from './indent';
import { TableKit } from '@tiptap/extension-table';
import { PageBreak } from './pageBreak';
import { Image } from './image';
import { LivePagination } from './livePagination';
import { CommentMark } from './commentMark';
import { CommentHistory } from './commentHistory';
import { Insertion, Deletion } from './trackChangesMarks';
import { TrackChanges } from './trackChanges';

// Pozn.: Underline, Link a Strike jsou v TipTap 3 součástí StarterKitu —
// neimportujeme je zvlášť (duplicitní registrace by házela chybu).

import { store } from '@/state/store';

// ═══════════════════════════════════════════════════════════════
// Editor jádro (TipTap / ProseMirror)
//
// Rozšíření jsou vybrána tak, aby odpovídala Word formátování a zároveň
// šla věrně mapovat na OOXML při exportu (viz src/docx/export.ts):
//   - StarterKit: odstavce, nadpisy (H1–6), seznamy, bold/italic, historie
//   - Underline / Strike (ve StarterKit) / Sub / Super
//   - TextStyle + Color + FontFamily: barva a font na úrovni runu
//   - Highlight: zvýraznění (w:highlight)
//   - TextAlign: zarovnání odstavce (w:jc)
//   - Link: hypertextové odkazy
// ═══════════════════════════════════════════════════════════════

export function createEditor(mount: HTMLElement): Editor {
  const editor = new Editor({
    element: mount,
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3, 4, 5, 6] },
        link: { openOnClick: false, autolink: true },
        // historie (undo/redo), underline, strike a link jsou součástí StarterKitu
      }),
      TextStyle,
      FontSize,
      Indent,
      Color,
      Highlight.configure({ multicolor: true }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      FontFamily,
      Subscript,
      Superscript,
      TableKit.configure({
        table: { resizable: true, HTMLAttributes: { class: 'doc-table' } },
      }),
      PageBreak,
      Image,
      LivePagination,
      CommentMark,
      CommentHistory,
      Insertion,
      Deletion,
      TrackChanges,
      Placeholder.configure({ placeholder: 'Začněte psát…' }),
    ],
    content: '<p></p>',
    autofocus: true,
    editorProps: {
      attributes: {
        // Nativní prohlížečový spell-check (MVP kontrola pravopisu, sekce 2.1).
        spellcheck: 'true',
        class: 'doc-page-content',
      },
    },
    onUpdate: ({ editor }) => {
      store.patchDoc({ dirty: true });
      updateWordCount(editor);
    },
    onCreate: ({ editor }) => {
      updateWordCount(editor);
    },
  });

  return editor;
}

/** Spočítá slova a zapíše do store (stavový řádek). */
export function updateWordCount(editor: Editor): void {
  const text = editor.getText().trim();
  const words = text ? text.split(/\s+/).length : 0;
  store.set({ wordCount: words });
}
